import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { TrovataAppState } from '@trovata/app/core/models/state.model';
import { checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError, StateApiError } from '@trovata/app/core/models/state-error.model';
import { combineLatest, Observable, throwError, firstValueFrom, take, filter } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
	Payment,
	BatchPayment,
	GetBatchPaymentsResponse,
	GetPaymentsV1Response,
	PaymentsListFilters,
	PAYMENTS_PAGINATION,
	GetPaymentRecordsResponse,
	PaymentRecord,
	PaymentsListQuery,
	PaymentsTab,
	isPayment,
	isBatchPayment,
	getPaymentRecordId,
} from '@trovata/app/features/payments/models/payment.model';
import { PaymentsHttpService } from '@trovata/app/features/payments/services/payments/payments-http.service';
import { PaymentsFacadeService } from '@trovata/app/features/payments/services/payments/facade/payments.facade.service';
import {
	SetStageFeedback,
	CreatePayment,
	SetPaymentsTabIndex,
	CancelPayment,
	ClearPaymentsState,
	ResetPaymentsState,
	InitPaymentsState,
	CreateBatchPayment,
	SetBatchStageFeedback,
	CancelBatchPayment,
	GetPaymentById,
	GetBatchPaymentById,
	GetChildPaymentsForBatch,
	UploadFileToPayment,
	DeleteFileFromPayment,
	RemovePaymentRecordFromStoreById,
	GetPaymentRecords,
	UpdatePaymentsFromBulkApproval,
	FetchChildPaymentsInBackground,
	UpdateBatchWithChildren,
	MarkBatchChildrenLoadingComplete,
} from '@trovata/app/features/payments/store/actions/payments.actions';
import { CustomerFeatureState } from '@trovata/app/features/settings/store/state/customer-feature.state';
import { PermissionId, PermissionMap } from '@trovata/app/features/settings/models/feature.model';
import { PaymentStatus } from '@trovata/app/features/payments/models/workflow.model';
import { ReadyForReviewAction, ReadyForReviewSnack, sortSnacksByDate } from '@trovata/app/shared/models/ready-for-review-snack.model';
import { TrovataResourceType, TrovataResourceViewText } from '@trovata/app/shared/models/trovata.model';
import { DateTime } from 'luxon';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PaymentType } from '@trovata/app/features/payments/models/template.model';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Container for default query configurations across all payment tabs.
 * Used during state initialization to set up consistent pagination and filtering.
 */
interface PaymentsTabQueries {
	readonly allPaymentsTableQuery: PaymentsListQuery;
	readonly openPaymentsTableQuery: PaymentsListQuery;
	readonly readyForReviewTableQuery: PaymentsListQuery;
}

/**
 * Represents the state transition of a payment record for count updates.
 *
 * Used to determine which tab count values need adjustment when a payment
 * is created, updated, or removed. Captures both previous and current state
 * to enable accurate count calculations.
 */
interface PaymentStateTransition {
	/** Whether this is a new record being added to the store */
	readonly isNewRecord: boolean;
	/** Whether the record was previously in OPEN status */
	readonly wasOpen: boolean;
	/** Whether the record is currently in OPEN status */
	readonly isOpen: boolean;
	/** Whether the record previously required review */
	readonly wasNeedsReview: boolean;
	/** Whether the record currently requires review */
	readonly isNeedsReview: boolean;
}

/**
 * PaymentsStateModel - State shape for the payments NGXS store.
 *
 * Defines all properties managed by PaymentsState including payment entities,
 * tab-specific ID arrays, query parameters, loading states, and error states.
 *
 * Uses entity normalization pattern (stores entities by ID in a map for fast lookups,
 * with separate arrays storing IDs to represent order/membership):
 * - paymentRecordEntities: Normalized entity map for O(1) lookups - constant time regardless
 *   of payment count, vs O(n) array search which slows as list grows (single source of truth)
 * - Tab-specific ID arrays: Maintain order and membership per tab
 * - Query objects: Store pagination, sorting, and filter state per tab
 * - Loading/error flags: Track async operation states
 */
export class PaymentsStateModel {
	/** Currently selected payments tab for navigation state. Null before initialization. */
	selectedTab: PaymentsTab | null;

	/** API errors for displaying error messages to users. Null before initialization. */
	apiErrors: StateApiError[] | null;

	/** Error state for All Payments table */
	allPaymentsError: boolean;

	/** Error state for Ready for Review table */
	reviewPaymentsError: boolean;

	/** Error state for Open Payments table */
	openPaymentsError: boolean;

	/** Query parameters for the All Payments table. Null before initialization. */
	allPaymentsTableQuery: PaymentsListQuery | null;

	/** Total record count for All Payments pagination */
	allPaymentsTableTotalCount: number;

	/** Query parameters for the Open Payments table. Null before initialization. */
	openPaymentsTableQuery: PaymentsListQuery | null;

	/** Total record count for Open Payments pagination */
	openPaymentsTableTotalCount: number;

	/** Query parameters for the Ready for Review table. Null before initialization. */
	readyForReviewTableQuery: PaymentsListQuery | null;

	/** Total record count for Ready for Review pagination */
	readyForReviewTableTotalCount: number;

	/** Loading state for the All Payments table */
	isAllPaymentsLoading: boolean;

	/** Loading state for the Open Payments table */
	isOpenPaymentsLoading: boolean;

	/** Loading state for the Ready for Review table */
	isReadyForReviewLoading: boolean;

	/** Loading state for review notification snacks (toast-style cards at bottom of screen that prompt users to review pending payments) */
	areSnacksLoading: boolean;

	/**
	 * Normalized entity storage (single source of truth).
	 * Maps unique IDs to payment records (Payment | BatchPayment).
	 * Key format: 'paymentId' or 'batchId'
	 */
	paymentRecordEntities: Record<string, PaymentRecord>;

	/**
	 * Array of entity IDs from "All Payments" table API call (all statuses).
	 * Maintains order and membership from the API response.
	 */
	allPaymentsTableIds: string[];

	/**
	 * Array of entity IDs for payments/batches that need user review.
	 */
	reviewPaymentsTableIds: string[];

	/**
	 * Array of entity IDs from "Open Payments" table API call (OPEN status only).
	 */
	openPaymentsTableIds: string[];

	/**
	 * Array of review notification snacks (toast-style cards) for payments that need user approval.
	 * Combined snacks for both Payment and BatchPayment types.
	 */
	paymentsSnacks: ReadyForReviewSnack[];

	/**
	 * Array of batch IDs currently loading child payments in the background.
	 * Used to show skeleton loaders in table cells for aggregated columns.
	 *
	 * Background loading allows tables to render immediately while child payments
	 * are fetched separately, improving perceived performance for large batches.
	 */
	batchIdsLoadingChildren: string[];

	/**
	 * Array of batch IDs where child payment fetching failed.
	 * Used to show "N/A" in table cells for aggregated columns.
	 */
	batchIdsWithChildLoadError: string[];
}

const DEFAULT_PAYMENTS_STATE_MODEL: PaymentsStateModel = {
	selectedTab: null,
	allPaymentsError: false,
	reviewPaymentsError: false,
	openPaymentsError: false,
	apiErrors: null,
	allPaymentsTableQuery: null,
	allPaymentsTableTotalCount: 0,
	openPaymentsTableQuery: null,
	openPaymentsTableTotalCount: 0,
	readyForReviewTableQuery: null,
	readyForReviewTableTotalCount: 0,
	isAllPaymentsLoading: true,
	isOpenPaymentsLoading: true,
	isReadyForReviewLoading: true,
	areSnacksLoading: true,
	paymentRecordEntities: {},
	allPaymentsTableIds: [],
	openPaymentsTableIds: [],
	reviewPaymentsTableIds: [],
	paymentsSnacks: [],
	batchIdsLoadingChildren: [],
	batchIdsWithChildLoadError: [],
};

/**
 * PaymentsState - Central state management for payments module.
 *
 * Manages payment records (single payments and batch payments), pagination,
 * tab-specific queries, and ready-for-review notifications.
 *
 * Key responsibilities:
 * - Stores payment entities in a normalized map for efficient updates
 * - Maintains separate ID arrays for each tab (all, open, ready for review)
 * - Handles pagination and sorting per tab with server-side queries
 * - Manages WebSocket update integration for real-time payment status changes
 * - Tracks batch payment child loading states for UI feedback
 *
 * Note: Payment entities persist in the map even after new table queries,
 * enabling detail page navigation without data loss.
 */
@State<PaymentsStateModel>({
	name: 'payments',
	defaults: DEFAULT_PAYMENTS_STATE_MODEL,
})
@Injectable()
export class PaymentsState {
	// ==========================================
	// Constants
	// ==========================================

	/** Default page size for pagination queries */
	private readonly defaultPaginationPageSize: number = PAYMENTS_PAGINATION.DEFAULT_PAGE_SIZE;

	/** Max parallel child payment fetches - 5 is a safe default that balances throughput without risking API rate limit throttling */
	private readonly childFetchConcurrencyLimit: number = 5;

	// ==========================================
	// Services (injected)
	// ==========================================

	private readonly paymentsHttpService: PaymentsHttpService = inject(PaymentsHttpService);
	private readonly paymentsFacade: PaymentsFacadeService = inject(PaymentsFacadeService);
	private readonly store: Store = inject(Store);
	private readonly sanitizer: DomSanitizer = inject(DomSanitizer);

	// ==========================================
	// Store Observables
	// ==========================================

	/** Observable of user's available permissions for authorization checks */
	private readonly userAvailablePermissions$: Observable<PermissionMap> = this.store.select(CustomerFeatureState.permissionIds);

	/** Observable of whether payments feature is enabled for the customer */
	private readonly paymentsEnabled$: Observable<boolean> = this.store.select(CustomerFeatureState.paymentsEnabled);

	/** Observable of application ready state for initialization timing */
	private readonly appReady$: Observable<boolean> = this.store.select((state: TrovataAppState) => state.core.appReady);

	// ==========================================
	// Selectors
	// ==========================================

	/** Returns whether the All Payments table has an error state. */
	@Selector()
	static allPaymentsError(state: PaymentsStateModel): boolean {
		return state.allPaymentsError;
	}

	/** Returns whether the Ready for Review table has an error state. */
	@Selector()
	static reviewPaymentsError(state: PaymentsStateModel): boolean {
		return state.reviewPaymentsError;
	}

	/** Returns whether the Open Payments table has an error state. */
	@Selector()
	static openPaymentsError(state: PaymentsStateModel): boolean {
		return state.openPaymentsError;
	}

	/**
	 * Returns the currently selected payments tab.
	 * @returns PaymentsTab | null - The active tab or null before initialization
	 */
	@Selector()
	static selectedTab(state: PaymentsStateModel): PaymentsTab | null {
		return state.selectedTab;
	}

	/**
	 * Returns array of API errors for the payments module.
	 * Used to display error messages to users when API calls fail.
	 * @returns StateApiError[] | null - Array of errors or null before initialization
	 */
	@Selector()
	static paymentsApiErrors(state: PaymentsStateModel): StateApiError[] | null {
		return state.apiErrors;
	}

	/**
	 * Returns the current query for the "All Payments" table.
	 * Includes pagination, sorting, date range, and status filters.
	 */
	@Selector()
	static allPaymentsTableQuery(state: PaymentsStateModel): PaymentsListQuery | null {
		return state.allPaymentsTableQuery;
	}

	/**
	 * Returns the current query for the "Ready for Review" table.
	 * Includes pagination, sorting, date range, and needsReview filter (status is always OPEN).
	 */
	@Selector()
	static readyForReviewTableQuery(state: PaymentsStateModel): PaymentsListQuery | null {
		return state.readyForReviewTableQuery;
	}

	/**
	 * Returns the current query for the "Open Payments" table.
	 * Includes pagination, sorting, and date range filters (status is always OPEN).
	 */
	@Selector()
	static openPaymentsTableQuery(state: PaymentsStateModel): PaymentsListQuery | null {
		return state.openPaymentsTableQuery;
	}

	/**
	 * Returns the total count of records matching "All Payments" table query.
	 * Used for pagination controls and display.
	 */
	@Selector()
	static allPaymentsTableTotalCount(state: PaymentsStateModel): number {
		return state.allPaymentsTableTotalCount;
	}

	/**
	 * Returns the total count of records matching "Ready for Review" table query.
	 * Used for pagination controls and display.
	 */
	@Selector()
	static readyForReviewTableTotalCount(state: PaymentsStateModel): number {
		return state.readyForReviewTableTotalCount;
	}

	/**
	 * Returns the total count of records matching "Open Payments" table query.
	 * Used for pagination controls and display.
	 */
	@Selector()
	static openPaymentsTableTotalCount(state: PaymentsStateModel): number {
		return state.openPaymentsTableTotalCount;
	}

	/** Returns whether the All Payments table is loading data. */
	@Selector()
	static isAllPaymentsLoading(state: PaymentsStateModel): boolean {
		return state.isAllPaymentsLoading;
	}

	/** Returns whether the Ready for Review table is loading data. */
	@Selector()
	static isReadyForReviewLoading(state: PaymentsStateModel): boolean {
		return state.isReadyForReviewLoading;
	}

	/** Returns whether the Open Payments table is loading data. */
	@Selector()
	static isOpenPaymentsLoading(state: PaymentsStateModel): boolean {
		return state.isOpenPaymentsLoading;
	}

	/** Returns whether the review notification snacks are loading. */
	@Selector()
	static areSnacksLoading(state: PaymentsStateModel): boolean {
		return state.areSnacksLoading;
	}

	/**
	 * Returns array of batch IDs currently loading child payments.
	 * Used to show skeleton loaders in table cells for aggregated column values.
	 */
	@Selector()
	static batchIdsLoadingChildren(state: PaymentsStateModel): string[] {
		return state.batchIdsLoadingChildren;
	}

	/**
	 * Returns array of batch IDs where child payment fetching failed.
	 * Used to show "N/A" in table cells for aggregated columns.
	 */
	@Selector()
	static batchIdsWithChildLoadError(state: PaymentsStateModel): string[] {
		return state.batchIdsWithChildLoadError;
	}

	/**
	 * Returns all payment records in the entities map as an array.
	 * Converts the Record<string, PaymentRecord> map to an array using Object.values(),
	 * filtering out any null/undefined entries that may exist from deleted records.
	 *
	 * Unlike allPaymentsTableRecords which filters by allPaymentsTableIds (tab-specific),
	 * this selector returns every record in paymentRecordEntities.
	 *
	 * Use cases:
	 * - Bulk payment page: accessing child payments that may not be in allPaymentsTableIds, etc.
	 * - Looking up any payment by iterating (though getPaymentById is preferred for single lookups)
	 */
	@Selector()
	static paymentRecordEntities(state: PaymentsStateModel): PaymentRecord[] {
		return Object.values(state.paymentRecordEntities).filter(Boolean);
	}

	/**
	 * Returns "All Payments" table records (both Payment and BatchPayment types).
	 * Derives array from normalized entity map using allPaymentsTableIds.
	 *
	 * NOTE: This selector returns payments for the "All Payments" tab only,
	 * filtered by allPaymentsTableIds. Use paymentRecordEntities for all entities.
	 */
	@Selector()
	static allPaymentsTableRecords(state: PaymentsStateModel): PaymentRecord[] {
		return state.allPaymentsTableIds.map((id: string) => state.paymentRecordEntities[id]).filter(Boolean);
	}

	/**
	 * Returns "Ready for Review" table records that need review (needsReview === true).
	 * Derives array from normalized entity map using reviewPaymentsTableIds.
	 */
	@Selector()
	static reviewPaymentsTableRecords(state: PaymentsStateModel): PaymentRecord[] {
		return state.reviewPaymentsTableIds.map((id: string) => state.paymentRecordEntities[id]).filter(Boolean);
	}

	/**
	 * Returns "Open Payments" table records (OPEN status only).
	 * Derives array from normalized entity map using openPaymentsTableIds.
	 */
	@Selector()
	static openPaymentsTableRecords(state: PaymentsStateModel): PaymentRecord[] {
		return state.openPaymentsTableIds.map((id: string) => state.paymentRecordEntities[id]).filter(Boolean);
	}

	/** Returns review notifications for both payments and batch payments. */
	@Selector()
	static paymentsSnacks(state: PaymentsStateModel): ReadyForReviewSnack[] {
		return state.paymentsSnacks;
	}

	/**
	 * O(1) lookup for a payment record by ID.
	 *
	 * This is a parameterized selector - it returns a function that takes a paymentRecordId parameter.
	 *
	 * @returns Function that takes paymentRecordId and returns PaymentRecord | undefined
	 *
	 * Usage:
	 *   // In a component or service (note the double parenthesis):
	 *   const paymentRecord: PaymentRecord | undefined = this.store.selectSnapshot(PaymentsState.getPaymentRecordById)(paymentRecordId);
	 *   if (paymentRecord) {
	 *     // Use payment data
	 *   }
	 *
	 *   // With observable (reactive):
	 *   this.paymentRecord$ = this.store.select(PaymentsState.getPaymentRecordById)(paymentRecordId);
	 *
	 *   // With signal (reactive, NGXS v4+):
	 *   this.paymentRecord = this.store.selectSignal(PaymentsState.getPaymentRecordById)(paymentRecordId);
	 */
	@Selector()
	static getPaymentRecordById(state: PaymentsStateModel): (paymentRecordId: string) => PaymentRecord | undefined {
		return (paymentRecordId: string): PaymentRecord | undefined => state.paymentRecordEntities[paymentRecordId];
	}

	// ==========================================
	// INITIALIZATION ACTIONS
	// ==========================================

	/**
	 * Initializes the payments state when the application starts.
	 *
	 * Waits for app readiness, permissions and payments feature flag.
	 * If payments are enabled and user has read permissions, initializes default state
	 * and dispatches actions to fetch initial data for all tables.
	 *
	 * @param context - NGXS state context
	 * @returns Promise<void>
	 *
	 * Side effects:
	 * - Dispatches GetPaymentRecords for all three payment tabs
	 * - Keeps default state if user lacks permissions
	 */
	@Action(InitPaymentsState)
	async initPaymentsState(context: StateContext<PaymentsStateModel>): Promise<void> {
		// Wait for all initialization dependencies:
		// - appReady: Core app bootstrap complete
		// - permissions: User permissions loaded for authorization check
		// - paymentsEnabled: Feature flag resolved (must be defined, not just truthy)
		// filter() blocks until all conditions met, take(1) completes after first valid emission
		const [, permissions, paymentsEnabled]: [boolean, PermissionMap, boolean] = await firstValueFrom(
			combineLatest([this.appReady$, this.userAvailablePermissions$, this.paymentsEnabled$]).pipe(
				filter(([ready, perms, enabled]: [boolean, PermissionMap, boolean]) => !!ready && !!perms && enabled !== undefined),
				take(1)
			)
		);

		if (!paymentsEnabled) {
			return;
		}

		if (permissions.has(PermissionId.readPayments)) {
			this.initAuthorizedPaymentsState(context);
		} else {
			const cleared: PaymentsStateModel = DEFAULT_PAYMENTS_STATE_MODEL;
			context.patchState(cleared);
		}
	}

	// ==========================================
	// QUERY/READ ACTIONS
	// ==========================================

	/**
	 * Fetches mixed payment type records (both Payment and BatchPayment types).
	 *
	 * Sets loading state before API call, processes response data through post-processing,
	 * and updates the normalized entity structure.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing query filters and tab type
	 * @returns Observable<GetPaymentRecordsResponse>
	 *
	 * Side effects:
	 * - Sets isAllPaymentsLoading, isReadyForReviewLoading or isOpenPaymentsLoading to true before call
	 * - Sets lazy loading batch child data arrays to empty arrays to cancel remaining requests
	 * - Updates entities map, ID arrays, snacks, and metadata on success
	 * - Sets loading to false after processing completes
	 * - Adds API error to state on failure
	 */
	@Action(GetPaymentRecords)
	getPaymentRecords(context: StateContext<PaymentsStateModel>, action: GetPaymentRecords): Observable<GetPaymentRecordsResponse> {
		// Set loading state to true BEFORE API call so UI shows spinner immediately
		// Clear batch loading arrays - in-flight child fetches check batchIdsLoadingChildren
		// before/after API calls and discard results if batch ID was removed (see fetchChildrenForSingleBatch)
		const loadingUpdate: Partial<PaymentsStateModel> = {
			batchIdsLoadingChildren: [],
			batchIdsWithChildLoadError: [],
		};
		if (action.tab === PaymentsTab.allPayments) {
			loadingUpdate.isAllPaymentsLoading = true;
		} else if (action.tab === PaymentsTab.readyForReview) {
			loadingUpdate.isReadyForReviewLoading = true;
		} else if (action.tab === PaymentsTab.openPayments) {
			loadingUpdate.isOpenPaymentsLoading = true;
		}
		context.patchState(loadingUpdate);

		return this.paymentsHttpService.getPaymentRecords(action.filters).pipe(
			tap((response: GetPaymentRecordsResponse) => {
				checkToRemoveStateApiError(context, GetPaymentRecords);
				this.postProcessPaymentRecords(context, response.payments, action.filters, response.totalCount, action.tab);
			}),
			catchError((error: HttpErrorResponse) => {
				// Set loading to false and error to true
				const errorUpdate: Partial<PaymentsStateModel> = {};
				if (action.tab === PaymentsTab.allPayments) {
					errorUpdate.isAllPaymentsLoading = false;
					errorUpdate.allPaymentsError = true;
				} else if (action.tab === PaymentsTab.readyForReview) {
					errorUpdate.isReadyForReviewLoading = false;
					errorUpdate.reviewPaymentsError = true;
				} else if (action.tab === PaymentsTab.openPayments) {
					errorUpdate.isOpenPaymentsLoading = false;
					errorUpdate.openPaymentsError = true;
				}
				context.patchState(errorUpdate);

				return throwError(() => {
					checkToAddStateApiError(error, context, GetPaymentRecords, 'Payments are down right now. Please try again later.');
				});
			})
		);
	}

	/**
	 * Fetches a single payment by ID.
	 *
	 * Used for payment detail pages and refreshing individual payment data.
	 * Updates the entity map with the latest payment data.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing paymentId
	 * @returns Observable<GetPaymentsV1Response>
	 *
	 * Side effects:
	 * - Updates payment in entities map with latest data
	 * - Throws error with user-friendly message on failure
	 */
	@Action(GetPaymentById)
	getPaymentById(context: StateContext<PaymentsStateModel>, action: GetPaymentById): Observable<GetPaymentsV1Response> {
		return this.paymentsHttpService.getPaymentById(action.paymentId).pipe(
			tap((getPaymentsResponse: GetPaymentsV1Response) => {
				const payment: Payment = getPaymentsResponse.payments[0];
				if (payment) {
					this.updateRecordInPaymentEntities(context, payment);
				}
			}),
			catchError((error: HttpErrorResponse) => throwError(() => checkForBadHttpErrorMessage(error, 'Payment is down right now. Please try again later.')))
		);
	}

	/**
	 * Fetches a single batch payment by ID.
	 *
	 * Used for bulk payment detail pages and refreshing individual batch payment data.
	 * Updates the entity map with the latest batch payment data.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing batchPaymentId
	 * @returns Observable<GetBatchPaymentsResponse>
	 *
	 * Side effects:
	 * - Updates batch payment in entities map with latest data
	 * - Throws error with user-friendly message on failure
	 */
	@Action(GetBatchPaymentById)
	getBatchPaymentById(context: StateContext<PaymentsStateModel>, action: GetBatchPaymentById): Observable<GetBatchPaymentsResponse> {
		return this.paymentsHttpService.getBatchPaymentById(action.batchPaymentId).pipe(
			tap((getBatchPaymentsResponse: GetBatchPaymentsResponse) => {
				const batchPayment: BatchPayment = getBatchPaymentsResponse.batches[0];
				if (batchPayment) {
					this.updateRecordInPaymentEntities(context, batchPayment);
				}
			}),
			catchError((error: HttpErrorResponse) => throwError(() => checkForBadHttpErrorMessage(error, 'Bulk payment is down right now. Please try again later.')))
		);
	}

	/**
	 * Fetches all child payments for a specific batch payment.
	 *
	 * Validates that the total number of payments matches the batch stats.
	 * Used when expanding a batch payment to view its constituent payments.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing batchPayment
	 * @returns Observable<GetPaymentsV1Response>
	 *
	 * Side effects:
	 * - Adds all child payments to entities map
	 * - Throws error if payment count mismatch
	 * - Adds API error to state on failure
	 */
	@Action(GetChildPaymentsForBatch)
	getChildPaymentsForBatch(context: StateContext<PaymentsStateModel>, action: GetChildPaymentsForBatch): Observable<GetPaymentsV1Response> {
		const totalChildPayments: number = action.batchPayment.stats.totalPayments;
		const body: PaymentsListFilters = { batchId: action.batchPayment.batchId };
		return this.paymentsHttpService.getPaginatedPayments(body).pipe(
			tap((getPaymentsV1Response: GetPaymentsV1Response) => {
				checkToRemoveStateApiError(context, GetChildPaymentsForBatch);
				const childPaymentsForBatch: Payment[] = getPaymentsV1Response.payments;
				if (totalChildPayments === childPaymentsForBatch.length) {
					// Process each payment and add to entity map
					childPaymentsForBatch.forEach((payment: Payment) => {
						this.updateRecordInPaymentEntities(context, payment);
					});
				} else {
					throw new Error(`Expected ${totalChildPayments} child payments but received ${childPaymentsForBatch.length}. Please try again later.`);
				}
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkToAddStateApiError(error, context, GetChildPaymentsForBatch, 'Bulk payments are down right now. Please try again later.'))
			)
		);
	}

	// ==========================================
	// CREATE ACTIONS
	// ==========================================

	/**
	 * Creates a new single payment.
	 *
	 * Sends payment data to the API and adds the newly created payment to the entity map.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing payment data to create
	 * @returns Observable<Payment>
	 *
	 * Side effects:
	 * - Adds new payment to entities map
	 * - May trigger ID array updates via updateRecordInPaymentEntities
	 * - Throws error with user-friendly message on failure
	 */
	@Action(CreatePayment)
	createPayment(context: StateContext<PaymentsStateModel>, action: CreatePayment): Observable<Payment> {
		return this.paymentsHttpService.createPayment(action.paymentToCreate).pipe(
			tap((payment: Payment) => {
				this.updateRecordInPaymentEntities(context, payment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Create payment is down right now. Please try again later.'))
			)
		);
	}

	/**
	 * Creates a new batch payment from an uploaded file.
	 *
	 * Sends the batch file to the API, adds the newly created batch payment to the entity map,
	 * and dispatches an action to fetch all child payments for the batch.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing batch file data
	 * @returns Observable<BatchPayment>
	 *
	 * Side effects:
	 * - Adds new batch payment to entities map
	 * - Dispatches GetChildPaymentsForBatch to fetch child payments
	 * - May trigger ID array updates via updateRecordInPaymentEntities
	 * - Throws error with user-friendly message on failure
	 */
	@Action(CreateBatchPayment)
	createBatchPayment(context: StateContext<PaymentsStateModel>, action: CreateBatchPayment): Observable<BatchPayment> {
		return this.paymentsHttpService.createBatchPayment(action.batchFile).pipe(
			tap((batchPayment: BatchPayment) => {
				this.updateRecordInPaymentEntities(context, batchPayment);
				context.dispatch(new GetChildPaymentsForBatch(batchPayment));
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Create batch payment is down right now. Please try again later.'))
			)
		);
	}

	// ==========================================
	// UPDATE ACTIONS
	// ==========================================

	/**
	 * Sets the currently selected payments tab.
	 *
	 * Simple state update action to track which tab the user is viewing.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing selected tab
	 *
	 * Side effects:
	 * - Updates selectedTab in state
	 */
	@Action(SetPaymentsTabIndex)
	setPaymentsTabIndex(context: StateContext<PaymentsStateModel>, action: SetPaymentsTabIndex): void {
		context.patchState({ selectedTab: action.selectedTab });
	}

	/**
	 * Sets approval/rejection feedback for a payment execution stage.
	 *
	 * Updates the payment with the user's approval decision and refreshes the entity map.
	 * This action is used during the workflow approval process.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing stage feedback
	 * @returns Observable<Payment>
	 *
	 * Side effects:
	 * - Updates payment in entities map with new workflow state
	 * - May remove payment from review arrays if approved/rejected
	 * - Throws error with user-friendly message on failure
	 */
	@Action(SetStageFeedback)
	setStageFeedback(context: StateContext<PaymentsStateModel>, action: SetStageFeedback): Observable<Payment> {
		return this.paymentsHttpService.setStageFeedback(action.stageFeedback).pipe(
			tap((payment: Payment) => {
				this.updateRecordInPaymentEntities(context, payment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Review payment is down right now. Please try again later.'))
			)
		);
	}

	/**
	 * Sets approval/rejection feedback for a batch payment execution stage.
	 *
	 * Updates the batch payment with the user's approval decision and refreshes the entity map.
	 * This action is used during the workflow approval process for bulk payments.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing batch stage feedback
	 * @returns Observable<BatchPayment>
	 *
	 * Side effects:
	 * - Updates batch payment in entities map with new workflow state
	 * - May remove batch payment from review arrays if approved/rejected
	 * - Throws error with user-friendly message on failure
	 */
	@Action(SetBatchStageFeedback)
	setBatchStageFeedback(context: StateContext<PaymentsStateModel>, action: SetBatchStageFeedback): Observable<BatchPayment> {
		return this.paymentsHttpService.setBatchStageFeedback(action.batchStageFeedback).pipe(
			tap((batchPayment: BatchPayment) => {
				this.updateRecordInPaymentEntities(context, batchPayment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Review bulk payment is down right now. Please try again later.'))
			)
		);
	}

	/**
	 * Updates state with successful payments from bulk approval.
	 * State-only action - no API call, just entity map updates.
	 *
	 * This action is dispatched by the facade after calling the bulk approval API directly.
	 * The facade needs the full API response (with errors) for UI handling, which NGXS dispatch
	 * doesn't provide, so the API call happens in the facade and state updates happen here.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing successful payment records to update
	 */
	@Action(UpdatePaymentsFromBulkApproval)
	updatePaymentsFromBulkApproval(context: StateContext<PaymentsStateModel>, action: UpdatePaymentsFromBulkApproval): void {
		if (action.payments?.length > 0) {
			action.payments.forEach((record: PaymentRecord) => {
				this.updateRecordInPaymentEntities(context, record);
			});
		}
	}

	/**
	 * Uploads a file attachment to an existing payment.
	 *
	 * Adds the uploaded file to the payment and updates the entity map with the refreshed payment data.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing file body and paymentId
	 * @returns Observable<Payment>
	 *
	 * Side effects:
	 * - Updates payment in entities map with new attachment
	 * - Throws error with user-friendly message on failure
	 */
	@Action(UploadFileToPayment)
	uploadFileToPayment(context: StateContext<PaymentsStateModel>, action: UploadFileToPayment): Observable<Payment> {
		return this.paymentsHttpService.uploadPaymentFile(action.body, action.paymentId).pipe(
			tap((payment: Payment) => {
				this.updateRecordInPaymentEntities(context, payment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Upload payment file is down right now. Please try again later.'))
			)
		);
	}

	/**
	 * Deletes a file attachment from an existing payment.
	 *
	 * Removes the specified file from the payment and updates the entity map with the refreshed payment data.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing attachmentId and paymentId
	 * @returns Observable<Payment>
	 *
	 * Side effects:
	 * - Updates payment in entities map with attachment removed
	 * - Throws error with user-friendly message on failure
	 */
	@Action(DeleteFileFromPayment)
	deleteFileFromPayment(context: StateContext<PaymentsStateModel>, action: DeleteFileFromPayment): Observable<Payment> {
		return this.paymentsHttpService.deleteAttachmentFromPayment(action.attachmentId, action.paymentId).pipe(
			tap((payment: Payment) => {
				this.updateRecordInPaymentEntities(context, payment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Delete payment file is down right now. Please try again later.'))
			)
		);
	}

	// ==========================================
	// CANCEL ACTIONS
	// ==========================================

	/**
	 * Cancels an existing payment.
	 *
	 * Sends cancellation request to the API and updates the entity map with the cancelled payment.
	 * The payment status will be updated to CANCELLED.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing payment cancellation data
	 * @returns Observable<Payment>
	 *
	 * Side effects:
	 * - Updates payment in entities map with CANCELLED status
	 * - May remove payment from review arrays
	 * - Throws error with user-friendly message on failure
	 */
	@Action(CancelPayment)
	cancelPayment(context: StateContext<PaymentsStateModel>, action: CancelPayment): Observable<Payment> {
		return this.paymentsHttpService.cancelPayment(action.paymentCancelation).pipe(
			tap((payment: Payment) => {
				this.updateRecordInPaymentEntities(context, payment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Cancel payment is down right now. Please try again later.'))
			)
		);
	}

	/**
	 * Cancels an existing batch payment.
	 *
	 * Sends cancellation request to the API and updates the entity map with the cancelled batch payment.
	 * The batch payment status will be updated to CANCELLED.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing batch payment cancellation data
	 * @returns Observable<BatchPayment>
	 *
	 * Side effects:
	 * - Updates batch payment in entities map with CANCELLED status
	 * - May remove batch payment from review arrays
	 * - Throws error with user-friendly message on failure
	 */
	@Action(CancelBatchPayment)
	cancelBatchPayment(context: StateContext<PaymentsStateModel>, action: CancelBatchPayment): Observable<BatchPayment> {
		return this.paymentsHttpService.cancelBatchPayment(action.batchPaymentCancelation).pipe(
			tap((batchPayment: BatchPayment) => {
				this.updateRecordInPaymentEntities(context, batchPayment);
			}),
			catchError((error: HttpErrorResponse) =>
				throwError(() => checkForBadHttpErrorMessage(error, 'Cancel bulk payment is down right now. Please try again later.'))
			)
		);
	}

	// ==========================================
	// STATE MANAGEMENT ACTIONS
	// ==========================================

	/**
	 * Clears all payments state data.
	 *
	 * Resets all state properties to their initial/empty values.
	 * Used when logging out or switching contexts.
	 *
	 * @param context - NGXS state context
	 *
	 * Side effects:
	 * - Clears entities map
	 * - Clears all ID arrays (allPaymentsTableIds, reviewPaymentsTableIds, openPaymentsTableIds)
	 * - Clears review notifications/snacks array
	 * - Resets all metadata (filters, counts, loading states)
	 * - Clears API errors and per-tab error flags
	 */
	@Action(ClearPaymentsState)
	clearPaymentsState(context: StateContext<PaymentsStateModel>): void {
		context.patchState(DEFAULT_PAYMENTS_STATE_MODEL);
	}

	/**
	 * Resets payments state by dispatching ClearPaymentsState.
	 *
	 * Convenience action that delegates to ClearPaymentsState.
	 * Provided for semantic clarity in different use cases.
	 *
	 * @param context - NGXS state context
	 *
	 * Side effects:
	 * - Dispatches ClearPaymentsState action
	 */
	@Action(ResetPaymentsState)
	resetPaymentsState(context: StateContext<PaymentsStateModel>): void {
		context.dispatch(new ClearPaymentsState());
	}

	/**
	 * Removes a specific payment record from the store by ID.
	 *
	 * Deletes the payment record from the entity map and removes its ID from all ID arrays.
	 * Used when a payment record no longer needs to be tracked.
	 *
	 * @param context - NGXS state context
	 * @param action - Action containing paymentRecordId to remove
	 *
	 * Side effects:
	 * - Removes payment record from entities map
	 * - Removes paymentRecordId from allPaymentsTableIds array
	 * - Removes paymentRecordId from openPaymentsTableIds array
	 * - Removes paymentRecordId from reviewPaymentsTableIds array
	 */
	@Action(RemovePaymentRecordFromStoreById)
	removePaymentRecordFromStoreById(context: StateContext<PaymentsStateModel>, action: RemovePaymentRecordFromStoreById): void {
		const state: PaymentsStateModel = context.getState();
		const record: PaymentRecord | undefined = state.paymentRecordEntities[action.paymentRecordId];

		// Decrement counts based on record state before removal
		if (record) {
			state.allPaymentsTableTotalCount = Math.max(0, state.allPaymentsTableTotalCount - 1);
			if (record.status === PaymentStatus.OPEN) {
				state.openPaymentsTableTotalCount = Math.max(0, state.openPaymentsTableTotalCount - 1);
			}
			if (record.needsReview && record.status === PaymentStatus.OPEN) {
				state.readyForReviewTableTotalCount = Math.max(0, state.readyForReviewTableTotalCount - 1);
			}
		}

		// Remove from entity map
		delete state.paymentRecordEntities[action.paymentRecordId];

		// Remove from all ID arrays
		state.allPaymentsTableIds = state.allPaymentsTableIds.filter((id: string) => id !== action.paymentRecordId);
		state.openPaymentsTableIds = state.openPaymentsTableIds.filter((id: string) => id !== action.paymentRecordId);
		state.reviewPaymentsTableIds = state.reviewPaymentsTableIds.filter((id: string) => id !== action.paymentRecordId);

		context.patchState(state);
	}

	// ==========================================
	// INITIALIZATION HELPERS
	// ==========================================

	/**
	 * Initializes the authorized default state for payments on app startup.
	 *
	 * Sets up initial filter values, default pagination, and dispatches actions
	 * to fetch initial data for all payments page tables.
	 *
	 * @param context - NGXS state context
	 *
	 * Side effects:
	 * - Initializes selectedTab to allPayments
	 * - Creates empty entities map and ID arrays
	 * - Sets default filters with 90-day date range
	 * - Dispatches GetPaymentRecords for all three payment tabs
	 */
	private initAuthorizedPaymentsState(context: StateContext<PaymentsStateModel>): void {
		const state: PaymentsStateModel = context.getState();
		state.selectedTab = PaymentsTab.allPayments;

		// Initialize normalized structure
		state.paymentRecordEntities = {};
		state.allPaymentsTableIds = [];
		state.openPaymentsTableIds = [];
		state.reviewPaymentsTableIds = [];
		state.paymentsSnacks = [];
		state.apiErrors = [];
		state.allPaymentsTableTotalCount = 0;
		state.openPaymentsTableTotalCount = 0;
		state.readyForReviewTableTotalCount = 0;

		// Set default table queries
		const { allPaymentsTableQuery, openPaymentsTableQuery, readyForReviewTableQuery }: PaymentsTabQueries = this.getDefaultTableQueries();
		state.allPaymentsTableQuery = allPaymentsTableQuery;
		state.openPaymentsTableQuery = openPaymentsTableQuery;
		state.readyForReviewTableQuery = readyForReviewTableQuery;

		context.patchState(state);

		// Fetch initial data for all three tables
		context.dispatch(new GetPaymentRecords(state.allPaymentsTableQuery, PaymentsTab.allPayments));
		context.dispatch(new GetPaymentRecords(state.openPaymentsTableQuery, PaymentsTab.openPayments));
		context.dispatch(new GetPaymentRecords(state.readyForReviewTableQuery, PaymentsTab.readyForReview));
	}

	/**
	 * Generates default filter configurations for all payments tabs.
	 *
	 * Creates filter objects with:
	 * - 90-day date range (from today back)
	 * - Default pagination settings
	 * - All payment types (ACH, WIRE, RTP, TRANSFER)
	 * - All statuses for allPayments tab, OPEN only for readyForReview and openPayments tabs
	 *
	 * @returns PaymentsTabQueries object containing allPaymentsTableQuery, openPaymentsTableQuery, and readyForReviewTableQuery
	 */
	private getDefaultTableQueries(): PaymentsTabQueries {
		// Calculate date range for last 90 days and set centralized date range
		const endDate: DateTime = DateTime.now();
		const startDate: DateTime = endDate.minus({ days: 90 });
		const initiatedDateRange: string = `between:${startDate.toISODate()},${endDate.toISODate()}`;
		// Define payment types and statuses for queries
		const types: PaymentType[] = [PaymentType.ACH, PaymentType.WIRE, PaymentType.RTP, PaymentType.TRANSFER, PaymentType.WITHDRAWAL];
		const statuses: PaymentStatus[] = [
			PaymentStatus.OPEN,
			PaymentStatus.SUBMITTED,
			PaymentStatus.PENDING,
			PaymentStatus.APPROVED,
			PaymentStatus.REJECTED,
			PaymentStatus.FAILED,
			PaymentStatus.PROCESSING,
			PaymentStatus.CANCELLED,
			PaymentStatus.CHANGE,
			PaymentStatus.ACCEPTED,
			PaymentStatus.COMPLETE,
			PaymentStatus.COMPLETED,
			PaymentStatus.REJECTED_BY_BANK,
		];
		// Create payment query filters
		const allPaymentsTableQuery: PaymentsListQuery = {
			from: 0,
			size: this.defaultPaginationPageSize,
			initiated: initiatedDateRange,
			sortKey: 'initiated',
			sortDir: 'desc',
			types: types,
			statuses: statuses,
		};
		const openPaymentsTableQuery: PaymentsListQuery = {
			from: 0,
			size: this.defaultPaginationPageSize,
			initiated: initiatedDateRange,
			sortKey: 'initiated',
			sortDir: 'desc',
			types: types,
			statuses: [PaymentStatus.OPEN],
		};
		const readyForReviewTableQuery: PaymentsListQuery = {
			from: 0,
			size: this.defaultPaginationPageSize,
			initiated: initiatedDateRange,
			sortKey: 'initiated',
			sortDir: 'desc',
			types: types,
			statuses: [PaymentStatus.OPEN],
			needsReview: true,
		};
		return { allPaymentsTableQuery, openPaymentsTableQuery, readyForReviewTableQuery };
	}

	// ==========================================
	// POST-PROCESSING HELPERS
	// ==========================================

	/**
	 * Consolidated post-processor for all payment tabs.
	 * Uses last-write-wins strategy for entity updates - if the same payment exists in the entity map
	 * from a previous API call, the newer response overwrites it. This ensures the UI always shows
	 * the most recent server state.
	 * Aggregates review notifications from any response containing needsReview records.
	 * Dispatches background fetch for child payments to avoid blocking table render.
	 *
	 * @param context - NGXS state context
	 * @param payments - Payment data received from API
	 * @param filters - Applied filters for this data set
	 * @param totalCount - Total count of records matching filters
	 * @param tab - The tab this data is for (determines which state properties to update)
	 */
	private postProcessPaymentRecords(
		context: StateContext<PaymentsStateModel>,
		payments: readonly PaymentRecord[],
		filters: PaymentsListQuery,
		totalCount: number,
		tab: PaymentsTab
	): void {
		const state: PaymentsStateModel = context.getState();

		// Step 1: Add all records to entity map (last-write-wins)
		const ids: string[] = this.addToPaymentEntities(state.paymentRecordEntities, payments);

		// Step 2: Build the state update object based on tab
		const stateUpdate: Partial<PaymentsStateModel> = {
			paymentRecordEntities: state.paymentRecordEntities,
		};

		switch (tab) {
			case PaymentsTab.allPayments:
				stateUpdate.allPaymentsTableIds = ids;
				stateUpdate.allPaymentsTableQuery = filters;
				stateUpdate.allPaymentsTableTotalCount = totalCount;
				stateUpdate.isAllPaymentsLoading = false;
				stateUpdate.allPaymentsError = false;
				break;
			case PaymentsTab.openPayments:
				stateUpdate.openPaymentsTableIds = ids;
				stateUpdate.openPaymentsTableQuery = filters;
				stateUpdate.openPaymentsTableTotalCount = totalCount;
				stateUpdate.isOpenPaymentsLoading = false;
				stateUpdate.openPaymentsError = false;
				break;
			case PaymentsTab.readyForReview:
				stateUpdate.reviewPaymentsTableIds = ids;
				stateUpdate.readyForReviewTableQuery = filters;
				stateUpdate.readyForReviewTableTotalCount = totalCount;
				stateUpdate.isReadyForReviewLoading = false;
				stateUpdate.reviewPaymentsError = false;
				break;
		}

		// Step 3: Identify batch payments that need child payments fetched
		const batchPayments: BatchPayment[] = payments.filter(isBatchPayment);
		const singlePayments: Payment[] = payments.filter(isPayment);

		// Optimization: skip child fetch if children already available from API response or previous load
		// Filter batches to determine which need child payments fetched.
		// Skip fetch if: (1) all children came in same API response, (2) children already loaded from previous fetch
		// Batches with totalChildPayments === 0 implicitly pass through without needing fetch
		const batchIdsNeedingChildren: string[] = batchPayments
			.filter((batchPayment: BatchPayment) => {
				// Find any child payments that came back in the same API response (matched by batchId)
				const existingChildPayments: Payment[] = singlePayments.filter((payment: Payment) => payment.batchId === batchPayment.batchId);
				const existingChildPaymentCount: number = existingChildPayments.length;
				const totalChildPayments: number = batchPayment.stats?.totalPayments ?? 0;

				// Case 1: All children already in API response - attach directly to avoid redundant fetch
				if (existingChildPaymentCount > 0 && existingChildPaymentCount >= totalChildPayments) {
					batchPayment.childPayments = existingChildPayments;
					return false; // No need to fetch
				}

				// Case 2: Children were loaded from a previous fetch - no need to refetch
				if (batchPayment.childPayments?.length >= totalChildPayments) {
					return false;
				}

				// Case 3: Batch has expected children that aren't loaded - needs fetch
				return totalChildPayments > 0;
			})
			.map((batchPayment: BatchPayment) => batchPayment.batchId);

		// Replace loading IDs with new ones only (cancels any in-flight requests for old batches)
		// Also clear error list since we're starting fresh with new batches
		stateUpdate.batchIdsLoadingChildren = batchIdsNeedingChildren;
		stateUpdate.batchIdsWithChildLoadError = [];

		// Step 4: Aggregate review notifications (from ANY response with needsReview records)
		const reviewRecords: PaymentRecord[] = payments.filter((r: PaymentRecord) => r.needsReview === true);
		if (reviewRecords.length > 0) {
			stateUpdate.paymentsSnacks = this.aggregateReviewNotifications(state.paymentsSnacks, reviewRecords);
		}

		stateUpdate.areSnacksLoading = false;

		// Patch state IMMEDIATELY - tables render now without waiting for child payments
		context.patchState(stateUpdate);

		// Step 5: Dispatch background fetch for child payments (non-blocking)
		if (batchIdsNeedingChildren.length > 0) {
			context.dispatch(new FetchChildPaymentsInBackground(batchIdsNeedingChildren));
		}
	}

	// ==========================================
	// BACKGROUND CHILD PAYMENT FETCHING
	// ==========================================

	/**
	 * Fetches child payments for batch payments in the background.
	 * Uses parallel fetching with a concurrency limit to avoid overwhelming the API.
	 * This action is dispatched by postProcessPaymentRecords after tables are rendered.
	 */
	@Action(FetchChildPaymentsInBackground)
	fetchChildPaymentsInBackground(context: StateContext<PaymentsStateModel>, action: FetchChildPaymentsInBackground): void {
		this.fetchChildrenWithConcurrencyLimit(context, action.batchIds, this.childFetchConcurrencyLimit);
	}

	/**
	 * Fetches child payments for multiple batches with a concurrency limit.
	 * Processes batches in chunks to avoid overwhelming the API.
	 *
	 * @param context - NGXS state context
	 * @param batchIds - Array of batch IDs to fetch children for
	 * @param limit - Maximum number of concurrent requests
	 */
	private async fetchChildrenWithConcurrencyLimit(context: StateContext<PaymentsStateModel>, batchIds: readonly string[], limit: number): Promise<void> {
		// Process in chunks: await each chunk's Promise.all before starting next chunk
		// This enforces the concurrency limit (e.g., max 5 requests at a time)
		for (let i: number = 0; i < batchIds.length; i += limit) {
			const chunk: string[] = batchIds.slice(i, i + limit);
			await Promise.all(chunk.map((batchId: string) => this.fetchChildrenForSingleBatch(context, batchId)));
		}
	}

	/**
	 * Fetches child payments for a single batch and dispatches update action.
	 * Includes relevancy checks to skip/discard if user initiated a new search.
	 *
	 * @param context - NGXS state context
	 * @param batchId - The batch ID to fetch children for
	 */
	private async fetchChildrenForSingleBatch(context: StateContext<PaymentsStateModel>, batchId: string): Promise<void> {
		// Check if batch is still relevant (user may have initiated a new search)
		const state: PaymentsStateModel = context.getState();
		if (!state.batchIdsLoadingChildren.includes(batchId)) {
			// Batch is no longer in loading list - user initiated new search, skip this fetch
			return;
		}

		try {
			const childPayments: Payment[] = await this.paymentsFacade.getPaymentsByBatchId(batchId);

			// Race condition prevention: user may have initiated new search during our API call
			const currentState: PaymentsStateModel = context.getState();
			if (!currentState.batchIdsLoadingChildren.includes(batchId)) {
				// Batch was removed during fetch - discard results
				return;
			}

			context.dispatch(new UpdateBatchWithChildren(batchId, childPayments));
		} catch (error: unknown) {
			// Check relevancy before logging error
			const currentState: PaymentsStateModel = context.getState();
			if (currentState.batchIdsLoadingChildren.includes(batchId)) {
				console.error(`Failed to fetch children for batch ${batchId}:`, error);
				context.dispatch(new MarkBatchChildrenLoadingComplete(batchId));
			}
		}
	}

	/**
	 * Updates a batch payment with its fetched child payments.
	 * Removes the batch from the loading list and updates the entity map.
	 */
	@Action(UpdateBatchWithChildren)
	updateBatchWithChildren(context: StateContext<PaymentsStateModel>, action: UpdateBatchWithChildren): void {
		const state: PaymentsStateModel = context.getState();
		const batchPayment: PaymentRecord | undefined = state.paymentRecordEntities[action.batchId];

		if (batchPayment && isBatchPayment(batchPayment)) {
			// Create mutable copy of entities map
			const updatedEntities: Record<string, PaymentRecord> = { ...state.paymentRecordEntities };

			// Add child payments to entities map (normalize)
			this.addToPaymentEntities(updatedEntities, action.childPayments);

			// Update batch with children reference
			const updatedBatch: BatchPayment = { ...batchPayment, childPayments: action.childPayments };
			updatedEntities[action.batchId] = updatedBatch;

			// Remove from loading set
			const updatedLoadingIds: string[] = state.batchIdsLoadingChildren.filter((id: string) => id !== action.batchId);

			context.patchState({
				paymentRecordEntities: updatedEntities,
				batchIdsLoadingChildren: updatedLoadingIds,
			});
		}
	}

	/**
	 * Marks a batch's child payment loading as complete (with error).
	 * Removes from loading list and adds to error list for UI indication.
	 */
	@Action(MarkBatchChildrenLoadingComplete)
	markBatchChildrenLoadingComplete(context: StateContext<PaymentsStateModel>, action: MarkBatchChildrenLoadingComplete): void {
		const state: PaymentsStateModel = context.getState();
		context.patchState({
			batchIdsLoadingChildren: state.batchIdsLoadingChildren.filter((id: string) => id !== action.batchId),
			// Add to error list so UI shows "N/A"
			batchIdsWithChildLoadError: [...state.batchIdsWithChildLoadError, action.batchId],
		});
	}

	// ==========================================
	// ENTITY NORMALIZATION HELPERS
	// ==========================================

	/**
	 * Adds records to payment entities map and returns array of their IDs.
	 * Overwrites existing entities with same ID (last-write-wins).
	 * Mutates the paymentRecordEntities parameter in place for performance.
	 * This is safe because callers pass a local copy before calling patchState(),
	 * so we're not mutating the actual store state directly.
	 *
	 * @param paymentRecordEntities - Payment entities map to update (mutated in place)
	 * @param records - Array of records to add
	 * @returns string[] - Array of IDs for added records (maintains input order)
	 *
	 * @example
	 * const paymentRecordEntities = {};
	 * const ids = addToPaymentEntities(paymentRecordEntities, [payment1, payment2]);
	 * // paymentRecordEntities now contains: { 'id1': payment1, 'id2': payment2 }
	 * // ids is: ['id1', 'id2']
	 */
	private addToPaymentEntities(paymentRecordEntities: Record<string, PaymentRecord>, records: readonly PaymentRecord[]): string[] {
		if (!paymentRecordEntities) {
			throw new Error('addToPaymentEntities: paymentRecordEntities map is null or undefined');
		}
		if (!records) {
			return [];
		}

		const ids: string[] = [];
		records.forEach((record: PaymentRecord) => {
			if (record) {
				const id: string = getPaymentRecordId(record);
				paymentRecordEntities[id] = record;
				ids.push(id);
			}
		});
		return ids;
	}

	/**
	 * Aggregates review notifications from new records into existing notifications.
	 * Removes duplicates and sorts by date.
	 *
	 * @param existingNotifications - Current array of review notifications
	 * @param newRecords - New records from API response that have needsReview=true
	 * @returns ReadyForReviewSnack[] - Merged and sorted array of review notifications
	 */
	private aggregateReviewNotifications(existingNotifications: readonly ReadyForReviewSnack[], newRecords: readonly PaymentRecord[]): ReadyForReviewSnack[] {
		// Use Set for O(1) duplicate detection instead of array.includes() which is O(n)
		const existingIds: Set<string> = new Set(existingNotifications.map((n: ReadyForReviewSnack) => n.resourceId));

		// Create notifications for new records not already in the set
		const newNotifications: ReadyForReviewSnack[] = newRecords
			.filter((record: PaymentRecord) => {
				const id: string = getPaymentRecordId(record);
				return !existingIds.has(id);
			})
			.map((record: PaymentRecord) => this.createNotificationForRecord(record));

		// Combine and sort
		return sortSnacksByDate([...existingNotifications, ...newNotifications]);
	}

	/**
	 * Creates a single review notification for a payment record.
	 *
	 * Generates HTML message content and resource metadata for display.
	 * Handles both Payment and BatchPayment types using type guards for safe property access.
	 *
	 * @param record - PaymentRecord to create notification for
	 * @returns ReadyForReviewSnack - Notification object for UI display
	 */
	private createNotificationForRecord(record: PaymentRecord): ReadyForReviewSnack {
		const requestor: string = 'A user ';
		const actionText: string = `${ReadyForReviewAction.initiated} a `;

		// Type-safe property access using type guards
		if (isPayment(record)) {
			const payment: Payment = record;

			const nameText: string = `payment (${payment.name})`;
			const additionalInfo: string = payment.instructions?.beneficiary?.name ? ` to ${payment.instructions.beneficiary.name}` : '';

			const htmlString: string = `
		<span class="font-700-14-20">${requestor}</span>
		<span class="font-400-14-20">${actionText}</span>
		<span class="font-700-14-20">${nameText}</span>
		${additionalInfo ? `<span class="font-400-14-20">${additionalInfo}</span>` : ''}
	`;

			// Safe to bypass sanitization: htmlString is constructed entirely from internal data (no user input)
			const messageSafeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(htmlString);

			return new ReadyForReviewSnack({
				date: DateTime.fromISO(payment.initiated).toLocaleString(DateTime.DATE_MED),
				messageHtml: messageSafeHtml,
				resource: record,
				resourceId: payment.paymentId,
				resourceType: TrovataResourceType.payment,
				resourceViewText: TrovataResourceViewText.Payment,
				sortByDate: payment.initiated,
			});
		} else {
			const batchPayment: BatchPayment = record;

			const nameText: string = `bulk payment (${batchPayment.name})`;

			const htmlString: string = `
		<span class="font-700-14-20">${requestor}</span>
		<span class="font-400-14-20">${actionText}</span>
		<span class="font-700-14-20">${nameText}</span>
	`;

			// Safe to bypass sanitization: htmlString is constructed entirely from internal data (no user input)
			const messageSafeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(htmlString);

			return new ReadyForReviewSnack({
				date: DateTime.fromISO(batchPayment.initiated).toLocaleString(DateTime.DATE_MED),
				messageHtml: messageSafeHtml,
				resource: record,
				resourceId: batchPayment.batchId,
				resourceType: TrovataResourceType.bulkPayment,
				resourceViewText: TrovataResourceViewText.BulkPayment,
				sortByDate: batchPayment.initiated,
			});
		}
	}

	/**
	 * Calculates the state transition between an existing record and an updated record.
	 * Used to determine which count values need adjustment.
	 *
	 * @param existingRecord - The current record in state (undefined if new)
	 * @param newRecord - The updated/new record
	 * @returns PaymentStateTransition - Object describing what changed
	 */
	private calculateStateTransition(existingRecord: PaymentRecord | undefined, newRecord: PaymentRecord): PaymentStateTransition {
		const isNewRecord: boolean = !existingRecord;
		const wasOpen: boolean = existingRecord?.status === PaymentStatus.OPEN;
		const wasNeedsReview: boolean = existingRecord?.needsReview === true;
		const isOpen: boolean = newRecord.status === PaymentStatus.OPEN;
		const isNeedsReview: boolean = newRecord.needsReview === true;

		return { isNewRecord, wasOpen, isOpen, wasNeedsReview, isNeedsReview };
	}

	/**
	 * Applies count adjustments based on payment state transitions.
	 *
	 * For new records, only increments counts if the record matches the respective
	 * table's filter query. For status changes, handles decrements for records leaving
	 * tables and increments for records entering tables. Prevents counts from going negative.
	 *
	 * @param state - The current state model (mutated directly)
	 * @param record - The payment or batch payment record
	 * @param transition - The state transition information
	 */
	private applyCountAdjustments(state: PaymentsStateModel, record: PaymentRecord, transition: PaymentStateTransition): void {
		// NEW RECORD: Only increment counts if record matches respective tab filters
		if (transition.isNewRecord) {
			// Only increment allPaymentsTable count if record matches its filters
			if (this.recordMatchesQuery(record, state.allPaymentsTableQuery)) {
				state.allPaymentsTableTotalCount++;
			}

			// Only increment openPaymentsTable count if record is open AND matches its filters
			if (transition.isOpen && this.recordMatchesQuery(record, state.openPaymentsTableQuery)) {
				state.openPaymentsTableTotalCount++;
			}

			// Only increment readyForReviewTable count if eligible AND matches its filters
			if (transition.isNeedsReview && transition.isOpen && this.recordMatchesQuery(record, state.readyForReviewTableQuery)) {
				state.readyForReviewTableTotalCount++;
			}
			return;
		}

		// EXISTING RECORD UPDATE: Handle both increments AND decrements
		// Use ID array membership (checked BEFORE arrays are updated) to know if record WAS in filtered set

		const id: string = getPaymentRecordId(record);

		// All Payments count: Symmetric logic for all payments (filtered by date/search/type, not status)
		const matchesAllPaymentsFilters: boolean = this.recordMatchesQuery(record, state.allPaymentsTableQuery);
		const wasInAllPayments: boolean = state.allPaymentsTableIds.includes(id);

		if (matchesAllPaymentsFilters && !wasInAllPayments) {
			state.allPaymentsTableTotalCount++;
		} else if (!matchesAllPaymentsFilters && wasInAllPayments) {
			state.allPaymentsTableTotalCount = Math.max(0, state.allPaymentsTableTotalCount - 1);
		}

		// Open count: Symmetric logic for open payments (filtered by OPEN status + query filters)
		const matchesOpenFilters: boolean = record.status === PaymentStatus.OPEN && this.recordMatchesQuery(record, state.openPaymentsTableQuery);
		const wasInOpenPayments: boolean = state.openPaymentsTableIds.includes(id);

		if (matchesOpenFilters && !wasInOpenPayments) {
			state.openPaymentsTableTotalCount++;
		} else if (!matchesOpenFilters && wasInOpenPayments) {
			state.openPaymentsTableTotalCount = Math.max(0, state.openPaymentsTableTotalCount - 1);
		}

		// Review count: Symmetric logic for reviewable payments (filtered by OPEN + needsReview + query filters)
		const isReviewable: boolean = record.needsReview && record.status === PaymentStatus.OPEN && this.recordMatchesQuery(record, state.readyForReviewTableQuery);
		const wasInReview: boolean = state.reviewPaymentsTableIds.includes(id);

		if (isReviewable && !wasInReview) {
			state.readyForReviewTableTotalCount++;
		} else if (!isReviewable && wasInReview) {
			state.readyForReviewTableTotalCount = Math.max(0, state.readyForReviewTableTotalCount - 1);
		}
	}

	/**
	 * Parses a date range string in "between:startDate,endDate" format.
	 *
	 * @param initiated - The initiated filter string (e.g., "between:2024-01-01,2024-03-31")
	 * @returns Object with startDate and endDate as DateTime, or null if parsing fails
	 */
	private parseDateRange(initiated: string): { startDate: DateTime; endDate: DateTime } | null {
		if (!initiated || !initiated.startsWith('between:')) {
			return null;
		}
		const dateRange: string = initiated.substring('between:'.length);
		const parts: string[] = dateRange.split(',');
		if (parts.length !== 2) {
			return null;
		}
		const startDate: DateTime = DateTime.fromISO(parts[0].trim()).startOf('day');
		const endDate: DateTime = DateTime.fromISO(parts[1].trim()).startOf('day');
		if (!startDate.isValid || !endDate.isValid) {
			return null;
		}
		return { startDate, endDate };
	}

	/**
	 * Extracts the payment type from a payment record.
	 * Handles the difference between Payment (uses 'type') and BatchPayment (uses 'paymentType').
	 *
	 * @param record - The payment record
	 * @returns The payment type, or undefined if not set
	 */
	private getRecordPaymentType(record: PaymentRecord): PaymentType | undefined {
		if (isPayment(record)) {
			return record.type;
		}
		return record.paymentType;
	}

	/**
	 * Checks if a payment record matches the given search text.
	 * Implements case-insensitive substring matching against searchable fields.
	 *
	 * Searchable fields match the backend API search logic:
	 * - Payment: paymentId, batchId, name, amount, valueDate, account.accountNumber,
	 *   instructions.beneficiary.accountNumber, instructions.beneficiary.name, instructions.beneficiary.iban
	 * - BatchPayment: batchId, name, amount, valueDate
	 *
	 * @param record - The payment record to check
	 * @param searchText - The search text to match against
	 * @returns true if any searchable field contains the search text (case-insensitive)
	 */
	private recordMatchesSearchText(record: PaymentRecord, searchText: string): boolean {
		if (!searchText || searchText.trim() === '') {
			return true;
		}

		const normalizedSearch: string = searchText.toLowerCase().trim();

		// Common searchable fields for both Payment and BatchPayment
		const commonFields: (string | null | undefined)[] = [record.name, record.amount?.toString(), record.valueDate];

		if (isPayment(record)) {
			// Payment-specific searchable fields
			const paymentFields: (string | null | undefined)[] = [
				record.paymentId,
				record.batchId,
				record.account?.accountNumber,
				record.instructions?.beneficiary?.accountNumber,
				record.instructions?.beneficiary?.name,
				record.instructions?.beneficiary?.iban,
			];
			return [...commonFields, ...paymentFields].some((field: string | null | undefined) => field?.toLowerCase().includes(normalizedSearch));
		} else {
			// BatchPayment-specific searchable fields
			const batchFields: (string | null | undefined)[] = [record.batchId];
			return [...commonFields, ...batchFields].some((field: string | null | undefined) => field?.toLowerCase().includes(normalizedSearch));
		}
	}

	/**
	 * Determines if a payment record matches the given query filters.
	 * Used to decide whether a record should remain in a tab's ID array after updates.
	 *
	 * Checks status, type, date range, and search text filters.
	 *
	 * @param record - The payment record to check
	 * @param query - The query filters to match against (may be null during initialization)
	 * @returns true if the record matches all specified filters, false otherwise
	 */
	private recordMatchesQuery(record: PaymentRecord, query: PaymentsListQuery | null): boolean {
		if (!query) {
			return true;
		}

		// Check status filter
		if (query.statuses && query.statuses.length > 0) {
			if (!query.statuses.includes(record.status)) {
				return false;
			}
		}

		// Check type filter
		if (query.types && query.types.length > 0) {
			const recordType: PaymentType | undefined = this.getRecordPaymentType(record);
			if (recordType && !query.types.includes(recordType)) {
				return false;
			}
		}

		// Check date range filter
		if (query.initiated) {
			const dateRange: { startDate: DateTime; endDate: DateTime } | null = this.parseDateRange(query.initiated);
			if (dateRange) {
				const recordDate: DateTime = DateTime.fromISO(record.initiated);
				if (!recordDate.isValid) {
					return false;
				}
				const recordDateOnly: DateTime = recordDate.startOf('day');
				if (recordDateOnly < dateRange.startDate || recordDateOnly > dateRange.endDate) {
					return false;
				}
			}
		}

		// Check search text filter
		if (query.search) {
			if (!this.recordMatchesSearchText(record, query.search)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Updates a record in the payment entities map and manages ID array membership.
	 *
	 * Handles both Payment and BatchPayment types uniformly. Updates the entity in the map,
	 * adds/removes the ID from appropriate ID arrays based on status, needsReview flags,
	 * and current query filters, and adjusts tab total counts to keep them synchronized.
	 *
	 * @param context - NGXS state context
	 * @param record - Updated record from API (must have needsReview already set)
	 *
	 * Side effects:
	 * - Updates or adds record to paymentRecordEntities map
	 * - Adds/removes from reviewPaymentsTableIds based on needsReview, OPEN status, AND query filters
	 * - Adds/removes from openPaymentsTableIds based on OPEN status AND query filters
	 * - Adds/removes from allPaymentsTableIds based on query filters
	 * - Adjusts total count values based on state transitions
	 */
	private updateRecordInPaymentEntities(context: StateContext<PaymentsStateModel>, record: PaymentRecord): void {
		const state: PaymentsStateModel = context.getState();
		const id: string = getPaymentRecordId(record);

		// Calculate state transition BEFORE updating entity
		const existingRecord: PaymentRecord | undefined = state.paymentRecordEntities[id];
		const transition: PaymentStateTransition = this.calculateStateTransition(existingRecord, record);

		// Apply count adjustments based on state transition
		this.applyCountAdjustments(state, record, transition);

		// Update entity map
		state.paymentRecordEntities[id] = record;

		// Update reviewPaymentsTableIds: Add/remove based on needsReview flag, open status, AND query filters
		const shouldBeInReview: boolean =
			record.needsReview && record.status === PaymentStatus.OPEN && this.recordMatchesQuery(record, state.readyForReviewTableQuery);
		const isInReview: boolean = state.reviewPaymentsTableIds.includes(id);

		if (shouldBeInReview && !isInReview) {
			state.reviewPaymentsTableIds.push(id);
		} else if (!shouldBeInReview && isInReview) {
			state.reviewPaymentsTableIds = state.reviewPaymentsTableIds.filter((rid: string) => rid !== id);
		}

		// Update paymentsSnacks: Remove snack if payment is no longer eligible for review
		if (!shouldBeInReview) {
			const snackIndex: number = state.paymentsSnacks.findIndex((snack: ReadyForReviewSnack) => snack.resourceId === id);
			if (snackIndex !== -1) {
				state.paymentsSnacks = [...state.paymentsSnacks.slice(0, snackIndex), ...state.paymentsSnacks.slice(snackIndex + 1)];
			}
		}

		// Update openPaymentsTableIds: Add/remove based on OPEN status AND query filters
		const matchesOpenPaymentsFilters: boolean = record.status === PaymentStatus.OPEN && this.recordMatchesQuery(record, state.openPaymentsTableQuery);
		const isInOpenPayments: boolean = state.openPaymentsTableIds.includes(id);

		if (matchesOpenPaymentsFilters && !isInOpenPayments) {
			state.openPaymentsTableIds.push(id);
		} else if (!matchesOpenPaymentsFilters && isInOpenPayments) {
			state.openPaymentsTableIds = state.openPaymentsTableIds.filter((rid: string) => rid !== id);
		}

		// Update allPaymentsTableIds: Add if matches filters, remove if no longer matches
		const matchesAllPaymentsFilters: boolean = this.recordMatchesQuery(record, state.allPaymentsTableQuery);
		const isInAllPayments: boolean = state.allPaymentsTableIds.includes(id);

		if (matchesAllPaymentsFilters && !isInAllPayments) {
			state.allPaymentsTableIds.push(id);
		} else if (!matchesAllPaymentsFilters && isInAllPayments) {
			state.allPaymentsTableIds = state.allPaymentsTableIds.filter((rid: string) => rid !== id);
		}

		context.patchState(state);
	}
}
